// ============================================================
// PartnershipForm.jsx
//
// Partnership application form for companies and organisations
// who want to collaborate with ProDevConsult.
//
// Features:
//   - Form data saved to Firestore "partnerships" collection
//   - PDF generation with jsPDF + autoTable
//   - PDF preview in a modal dialog
//   - PDF download
//   - File attachment support
//
// Clean Code:
//   - BRAND colors centralized
//   - INITIAL_FORM extracted for clean resets
//   - FORM_SECTIONS drives all field rendering via .map()
//   - alert() replaced with Snackbar
// ============================================================

import React, { useState } from "react";
import {
    TextField, Button, Grid, Card, CardContent,
    Typography, Container, Dialog, DialogTitle,
    DialogContent, DialogActions, Box, styled,
    Snackbar, Alert, Chip, Divider, CircularProgress,
    InputAdornment,
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────────
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LanguageIcon from "@mui/icons-material/Language";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import WorkIcon from "@mui/icons-material/Work";
import HandshakeIcon from "@mui/icons-material/Handshake";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FactoryIcon from "@mui/icons-material/Factory";

// ── Firebase ─────────────────────────────────────────────────
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

// ── PDF Generation ───────────────────────────────────────────
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ============================================================
//  CONSTANTS
// ============================================================

/**
 * Brand colors — consistent with the rest of the platform.
 */
const BRAND = {
    dark:  "#100720",
    gold:  "#E5C95F",
    white: "#FFFFFF",
    muted: "#555",
    border: "rgba(0,0,0,0.08)",
};

/** Background image */
const BG_IMAGE = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/**
 * INITIAL_FORM
 * Default empty form state.
 * Extracted so we can reset cleanly after submission
 * without duplicating the shape in multiple places.
 */
const INITIAL_FORM = {
    companyName:     "",
    industry:        "",
    email:           "",
    phone:           "",
    website:         "",
    address:         "",
    contactPerson:   "",
    designation:     "",
    partnershipType: "",
    proposal:        "",
};

/**
 * FORM_SECTIONS
 * Drives all field rendering in the form.
 * Grouped into three sections for visual clarity:
 *   1. Company Information
 *   2. Contact Information
 *   3. Partnership Details
 *
 * Each field: { name, label, type, required, icon, sm, multiline?, rows? }
 */
const FORM_SECTIONS = [
    {
        title: "Company Information",
        icon:  <BusinessIcon fontSize="small" />,
        fields: [
            { name: "companyName", label: "Company Name",    type: "text", required: true,  sm: 12, icon: <BusinessIcon /> },
            { name: "industry",    label: "Industry",         type: "text", required: true,  sm: 6,  icon: <FactoryIcon /> },
            { name: "website",     label: "Website URL",      type: "url",  required: false, sm: 6,  icon: <LanguageIcon /> },
            { name: "address",     label: "Company Address",  type: "text", required: false, sm: 12, icon: <LocationOnIcon />, multiline: true, rows: 2 },
        ],
    },
    {
        title: "Contact Information",
        icon:  <PersonIcon fontSize="small" />,
        fields: [
            { name: "contactPerson", label: "Contact Person", type: "text",  required: true,  sm: 6, icon: <PersonIcon /> },
            { name: "designation",   label: "Designation",    type: "text",  required: false, sm: 6, icon: <WorkIcon /> },
            { name: "email",         label: "Email Address",  type: "email", required: true,  sm: 6, icon: <EmailIcon /> },
            { name: "phone",         label: "Phone Number",   type: "tel",   required: true,  sm: 6, icon: <PhoneIcon /> },
        ],
    },
    {
        title: "Partnership Details",
        icon:  <HandshakeIcon fontSize="small" />,
        fields: [
            { name: "partnershipType", label: "Partnership Type (e.g. Strategic, Technical, Financial)", type: "text", required: false, sm: 12, icon: <HandshakeIcon /> },
            { name: "proposal",        label: "Business Proposal",  type: "text", required: true,  sm: 12, icon: <DescriptionIcon />, multiline: true, rows: 5 },
        ],
    },
];

// ============================================================
//  STYLED COMPONENTS
// ============================================================

/**
 * PageWrapper
 * Full-page background wrapper with white image tint.
 */
const PageWrapper = styled(Box)({
    minHeight: "100vh",
    paddingTop: "120px",
    paddingBottom: "60px",
    backgroundImage: `linear-gradient(rgba(255,255,255,0.88), rgba(255,255,255,0.88)), url(${BG_IMAGE})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    fontFamily: "'Poppins', sans-serif",
});

/**
 * FormCard
 * Centered glassmorphism card containing the form.
 */
const FormCard = styled(Card)({
    width: "100%",
    padding: "40px 36px",
    borderRadius: "20px",
    backgroundColor: "rgba(255,255,255,0.97)",
    backdropFilter: "blur(8px)",
    border: `1px solid ${BRAND.border}`,
    boxShadow: "0 8px 40px rgba(0,0,0,0.07)",
});

/**
 * SectionLabel
 * Heading row for each form section group.
 */
const SectionLabel = styled(Box)({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 14px",
    backgroundColor: BRAND.dark,
    borderRadius: "10px",
    marginBottom: "8px",
    marginTop: "8px",
});

/**
 * PrimaryButton
 * Dark branded button — used for Submit.
 */
const PrimaryButton = styled(Button)({
    marginTop: "8px",
    backgroundColor: BRAND.dark,
    color: BRAND.gold,
    fontWeight: 700,
    fontSize: "0.95rem",
    padding: "12px",
    borderRadius: "10px",
    textTransform: "none",
    transition: "background-color 0.2s ease, transform 0.2s ease",
    "&:hover": {
        backgroundColor: "#1a0b3a",
        transform: "translateY(-1px)",
    },
    "&:disabled": { backgroundColor: "#ccc", color: "#888" },
});

/**
 * OutlineButton
 * Outlined secondary button — used for PDF Preview.
 */
const OutlineButton = styled(Button)({
    marginTop: "8px",
    borderColor: BRAND.gold,
    color: BRAND.dark,
    fontWeight: 700,
    fontSize: "0.95rem",
    padding: "12px",
    borderRadius: "10px",
    textTransform: "none",
    "&:hover": {
        borderColor: BRAND.dark,
        backgroundColor: "rgba(16,7,32,0.04)",
    },
});

// ============================================================
//  PDF HELPER
// ============================================================

/**
 * generatePdf
 * Creates a branded PDF document from the form data.
 * Uses jsPDF + autoTable for the table layout.
 *
 * @param {Object} formData - The current form values
 * @param {File|null} file  - The attached file (used for Yes/No note)
 * @returns {jsPDF} The generated PDF document instance
 */
const generatePdf = (formData, file) => {
    const doc = new jsPDF();

    // Dark header band
    doc.setFillColor(16, 7, 32);
    doc.rect(0, 0, 210, 32, "F");
    doc.setTextColor(229, 201, 95); // BRAND gold in RGB
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("ProDevConsult — Partnership Application", 105, 21, { align: "center" });

    // Data table
    autoTable(doc, {
        head: [["Field", "Value"]],
        body: Object.entries(formData).map(([key, value]) => [
            // Convert camelCase keys to readable labels
            key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()),
            value || "—",
        ]),
        startY: 42,
        styles: { fontSize: 10, cellPadding: 4 },
        headStyles: { fillColor: [16, 7, 32], textColor: [229, 201, 95], fontStyle: "bold" },
        alternateRowStyles: { fillColor: [250, 250, 250] },
    });

    // File attachment note
    doc.setFontSize(11);
    doc.setTextColor(80, 80, 80);
    doc.text(
        `Supporting Document: ${file ? `Yes — ${file.name}` : "No"}`,
        14,
        doc.lastAutoTable.finalY + 14
    );

    // Dark footer band
    doc.setFillColor(16, 7, 32);
    doc.rect(0, 280, 210, 17, "F");
    doc.setTextColor(229, 201, 95);
    doc.setFontSize(9);
    doc.text("© ProDevConsult | Confidential | www.prodevconsult.com", 105, 289, { align: "center" });

    return doc;
};

// ============================================================
//  MAIN COMPONENT
// ============================================================

export default function PartnershipForm() {

    // ── State ────────────────────────────────────────────────
    const [formData,    setFormData]    = useState(INITIAL_FORM);
    const [file,        setFile]        = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [pdfUrl,      setPdfUrl]      = useState("");
    const [submitting,  setSubmitting]  = useState(false);
    const [submitted,   setSubmitted]   = useState(false);
    const [error,       setError]       = useState("");

    // ============================================================
    //  HANDLERS
    // ============================================================

    /**
     * handleChange
     * Generic handler for all text fields.
     */
    const handleChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });

    /**
     * handleFileChange
     * Stores the selected file in state for PDF reference.
     */
    const handleFileChange = (e) => setFile(e.target.files[0]);

    /**
     * handleSubmit
     * Saves form data to Firestore "partnerships" collection.
     * Adds server timestamp. Resets form on success.
     * Shows Snackbar instead of alert().
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await addDoc(collection(db, "partnerships"), {
                ...formData,
                submittedAt: serverTimestamp(),
            });
            setSubmitted(true);
            setFormData(INITIAL_FORM);
            setFile(null);
        } catch (err) {
            console.error("Error submitting form:", err);
            setError("Failed to submit. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    /**
     * handlePreview
     * Generates the PDF and opens a preview dialog.
     */
    const handlePreview = () => {
        const doc = generatePdf(formData, file);
        const blob = doc.output("blob");
        setPdfUrl(URL.createObjectURL(blob));
        setPreviewOpen(true);
    };

    /**
     * handleDownload
     * Generates the PDF and triggers a file download.
     */
    const handleDownload = () => {
        generatePdf(formData, file).save("ProDevConsult_Partnership_Application.pdf");
        setPreviewOpen(false);
    };

    // ============================================================
    //  RENDER
    // ============================================================

    return (
        <PageWrapper>
            <Container maxWidth="md">
                <FormCard>

                    {/* ── Form Header ── */}
                    <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                        <Box sx={{
                            width: 52, height: 52, borderRadius: "14px",
                            backgroundColor: BRAND.dark,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            flexShrink: 0,
                        }}>
                            <HandshakeIcon sx={{ color: BRAND.gold, fontSize: 28 }} />
                        </Box>
                        <Box>
                            <Typography
                                variant="h5" fontWeight={900}
                                sx={{ color: BRAND.dark, fontFamily: "'Georgia', serif", lineHeight: 1.2 }}
                            >
                                Partnership Application
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                ProDevConsult — Strategic & Technical Partnerships
                            </Typography>
                        </Box>
                    </Box>

                    <Typography variant="body2" sx={{ color: BRAND.muted, mb: 2, lineHeight: 1.7 }}>
                        Fill out the form below to submit your partnership proposal.
                        You can preview and download a PDF copy before submitting.
                    </Typography>

                    {/* Partnership type chips */}
                    <Box display="flex" flexWrap="wrap" gap={1} mb={3}>
                        {["Strategic", "Technical", "Financial", "Educational"].map((type) => (
                            <Chip
                                key={type} label={type} size="small"
                                sx={{
                                    backgroundColor: "rgba(16,7,32,0.06)",
                                    color: BRAND.dark, fontWeight: 600, fontSize: "0.72rem",
                                }}
                            />
                        ))}
                    </Box>

                    <Divider sx={{ mb: 3, borderColor: BRAND.border }} />

                    {/* ── Form ── */}
                    <Box component="form" onSubmit={handleSubmit}>
                        <Grid container spacing={2.5}>

                            {/* Render sections and fields from FORM_SECTIONS */}
                            {FORM_SECTIONS.map((section) => (
                                <React.Fragment key={section.title}>

                                    {/* Section heading */}
                                    <Grid item xs={12}>
                                        <SectionLabel>
                                            <Box sx={{ color: BRAND.gold, display: "flex" }}>
                                                {section.icon}
                                            </Box>
                                            <Typography
                                                variant="body2" fontWeight={700}
                                                sx={{ color: BRAND.white, letterSpacing: "0.5px" }}
                                            >
                                                {section.title}
                                            </Typography>
                                        </SectionLabel>
                                    </Grid>

                                    {/* Section fields */}
                                    {section.fields.map((field) => (
                                        <Grid item xs={12} sm={field.sm} key={field.name}>
                                            <TextField
                                                label={field.label}
                                                name={field.name}
                                                type={field.type}
                                                value={formData[field.name]}
                                                onChange={handleChange}
                                                fullWidth
                                                required={field.required}
                                                multiline={field.multiline || false}
                                                rows={field.rows || 1}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Box sx={{ color: BRAND.gold, display: "flex" }}>
                                                                {field.icon}
                                                            </Box>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                    ))}
                                </React.Fragment>
                            ))}

                            {/* ── File Upload ── */}
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        border: `2px dashed ${BRAND.gold}`,
                                        borderRadius: "12px",
                                        p: 2.5,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        backgroundColor: "rgba(229,201,95,0.04)",
                                        cursor: "pointer",
                                    }}
                                    component="label"
                                >
                                    <UploadFileIcon sx={{ color: BRAND.gold, fontSize: 32 }} />
                                    <Box>
                                        <Typography fontWeight={700} color={BRAND.dark} fontSize="0.9rem">
                                            {file ? file.name : "Attach Supporting Document"}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            PDF or image files accepted (optional)
                                        </Typography>
                                    </Box>
                                    <input
                                        type="file"
                                        accept="application/pdf,image/*"
                                        onChange={handleFileChange}
                                        hidden
                                    />
                                </Box>
                            </Grid>

                            {/* ── Action Buttons ── */}
                            <Grid item xs={12} sm={6}>
                                <PrimaryButton
                                    type="submit" variant="contained" fullWidth
                                    disabled={submitting}
                                    startIcon={
                                        submitting
                                            ? <CircularProgress size={18} sx={{ color: BRAND.gold }} />
                                            : <SendIcon />
                                    }
                                >
                                    {submitting ? "Submitting..." : "Submit Application"}
                                </PrimaryButton>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <OutlineButton
                                    variant="outlined" fullWidth
                                    onClick={handlePreview}
                                    startIcon={<PictureAsPdfIcon />}
                                >
                                    Preview PDF
                                </OutlineButton>
                            </Grid>

                        </Grid>
                    </Box>
                </FormCard>
            </Container>

            {/* ── PDF Preview Dialog ── */}
            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                fullWidth maxWidth="md"
                PaperProps={{ sx: { borderRadius: "16px" } }}
            >
                <DialogTitle sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    backgroundColor: BRAND.dark, color: BRAND.gold,
                    fontWeight: 800,
                }}>
                    <PictureAsPdfIcon />
                    Preview Partnership Application
                </DialogTitle>

                <DialogContent dividers sx={{ p: 0 }}>
                    {pdfUrl && (
                        <iframe
                            src={pdfUrl}
                            title="PDF Preview"
                            width="100%"
                            height="600px"
                            style={{ border: "none", display: "block" }}
                        />
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button
                        onClick={() => setPreviewOpen(false)}
                        sx={{ color: BRAND.muted, fontWeight: 600 }}
                    >
                        Close
                    </Button>
                    <Button
                        onClick={handleDownload}
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        sx={{
                            backgroundColor: BRAND.dark,
                            color: BRAND.gold,
                            fontWeight: 700,
                            borderRadius: "8px",
                            "&:hover": { backgroundColor: "#1a0b3a" },
                        }}
                    >
                        Download PDF
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Success Snackbar ── */}
            <Snackbar
                open={submitted} autoHideDuration={5000}
                onClose={() => setSubmitted(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSubmitted(false)} severity="success"
                    icon={<CheckCircleIcon />}
                    sx={{ backgroundColor: "#10b981", color: BRAND.white, fontWeight: 600 }}
                >
                    Partnership application submitted! We will be in touch shortly.
                </Alert>
            </Snackbar>

            {/* ── Error Snackbar ── */}
            <Snackbar
                open={!!error} autoHideDuration={5000}
                onClose={() => setError("")}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setError("")} severity="error"
                    sx={{ backgroundColor: "#d32f2f", color: BRAND.white, fontWeight: 600 }}
                >
                    {error}
                </Alert>
            </Snackbar>

        </PageWrapper>
    );
}
